import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Link2, Check, Loader2, Users, Search, UserPlus, 
  AlertCircle, X, CreditCard, Eye, EyeOff, ChevronRight, ChevronDown,
  Sparkles, Ban, RefreshCw, ExternalLink, Zap
} from 'lucide-react';
import * as api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Find Match Modal Component (same as Stripe version)
const FindMatchModal = ({ gcCustomer, onClose, onLink }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLinking, setIsLinking] = useState(false);

  useEffect(() => {
    loadMatches();
  }, [searchTerm]);

  const loadMatches = async () => {
    setIsLoading(true);
    try {
      const token = api.getToken();
      const url = searchTerm 
        ? `${API_URL}/api/integrations/gocardless/customers/${gcCustomer.id}/matches?search=${encodeURIComponent(searchTerm)}`
        : `${API_URL}/api/integrations/gocardless/customers/${gcCustomer.id}/matches`;
      
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setMatches(data.matches || []);
      }
    } catch (err) {
      console.error('Error loading matches:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLink = async (crmCustomerId) => {
    setIsLinking(true);
    try {
      await onLink(gcCustomer.id, crmCustomerId);
      onClose();
    } catch (err) {
      console.error('Error linking:', err);
    } finally {
      setIsLinking(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 safe-area-all">
      <div className="bg-white rounded-3xl w-full max-w-2xl modal-shell-h flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Find Matching Customer</h2>
              <p className="text-teal-200 text-sm mt-1">
                Link "{gcCustomer.name}" to a CRM customer
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-zinc-200">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
            </div>
          ) : matches.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p>No matching customers found</p>
              <p className="text-sm mt-1">Try a different search term</p>
            </div>
          ) : (
            <div className="space-y-3">
              {matches.map((match) => {
                const isAlreadyLinked = match.already_linked || match.gocardless_customer_id;
                return (
                  <div 
                    key={match.id}
                    className={`border rounded-xl p-4 transition-all ${
                      isAlreadyLinked 
                        ? 'border-amber-300 bg-amber-50/50' 
                        : 'border-zinc-200 hover:border-teal-300 hover:bg-teal-50/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold ${
                          isAlreadyLinked 
                            ? 'bg-amber-100 text-amber-700' 
                            : 'bg-teal-100 text-teal-700'
                        }`}>
                          {match.name?.charAt(0) || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{match.name}</p>
                          <p className="text-sm text-zinc-500">{match.email || match.address || 'No details'}</p>
                          {isAlreadyLinked && (
                            <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Already linked to another GoCardless customer
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {match.match_score > 0 && (
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            match.match_score >= 80 ? 'bg-teal-100 text-teal-700' :
                            match.match_score >= 50 ? 'bg-amber-100 text-amber-700' :
                            'bg-zinc-100 text-zinc-600'
                          }`}>
                            {match.match_score}% match
                          </span>
                        )}
                        {isAlreadyLinked ? (
                          <span className="px-4 py-2 bg-zinc-200 text-zinc-500 text-sm rounded-xl font-medium flex items-center gap-2">
                            <Ban size={14} />
                            Linked
                          </span>
                        ) : (
                          <button
                            onClick={() => handleLink(match.id)}
                            disabled={isLinking}
                            className="px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-zinc-300 text-white text-sm rounded-xl font-medium transition-colors flex items-center gap-2"
                          >
                            {isLinking ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}
                            Link
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Match Preview Modal Component
const MatchPreviewModal = ({ gcCustomers, onClose, onLinkAll }) => {
  const [selectedMatches, setSelectedMatches] = useState(new Set());
  
  const customersWithMatches = gcCustomers.filter(c => 
    !c.is_linked && 
    c.potential_match && 
    !c.potential_match.already_linked
  );
  
  useEffect(() => {
    setSelectedMatches(new Set(customersWithMatches.map(c => c.id)));
  }, []);
  
  const toggleMatch = (gcId) => {
    setSelectedMatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(gcId)) {
        newSet.delete(gcId);
      } else {
        newSet.add(gcId);
      }
      return newSet;
    });
  };
  
  const selectAll = () => {
    setSelectedMatches(new Set(customersWithMatches.map(c => c.id)));
  };
  
  const selectNone = () => {
    setSelectedMatches(new Set());
  };
  
  const handleLinkSelected = () => {
    const matchesToLink = customersWithMatches
      .filter(c => selectedMatches.has(c.id))
      .map(c => ({
        gcId: c.id,
        gcName: c.name || c.email,
        crmId: c.potential_match.id,
        crmName: c.potential_match.name,
        score: c.potential_match.match_score
      }));
    
    onLinkAll(matchesToLink);
  };
  
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 safe-area-all">
      <div className="bg-white rounded-3xl w-full max-w-3xl modal-shell-h flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Review All Matches</h2>
              <p className="text-teal-200 text-sm mt-1">
                {customersWithMatches.length} GoCardless customers matched to CRM records
              </p>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-xl transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        
        {/* Selection Controls */}
        <div className="px-6 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={selectAll}
              className="text-sm text-teal-600 hover:text-teal-800 font-medium"
            >
              Select All
            </button>
            <button 
              onClick={selectNone}
              className="text-sm text-zinc-500 hover:text-zinc-700 font-medium"
            >
              Select None
            </button>
          </div>
          <p className="text-sm text-zinc-600">
            <span className="font-semibold text-teal-600">{selectedMatches.size}</span> of {customersWithMatches.length} selected
          </p>
        </div>
        
        {/* Match List */}
        <div className="flex-1 overflow-y-auto p-4">
          {customersWithMatches.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <Users size={48} className="mx-auto mb-3 opacity-30" />
              <p>No automatic matches found</p>
              <p className="text-sm mt-1">You can manually link customers from the main list</p>
            </div>
          ) : (
            <div className="space-y-2">
              {customersWithMatches.map((customer) => {
                const isSelected = selectedMatches.has(customer.id);
                const match = customer.potential_match;
                
                return (
                  <div 
                    key={customer.id}
                    onClick={() => toggleMatch(customer.id)}
                    className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                      isSelected 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-zinc-200 bg-white hover:border-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Checkbox */}
                      <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected 
                          ? 'bg-teal-500 border-teal-500' 
                          : 'border-zinc-300'
                      }`}>
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      
                      {/* GoCardless Customer */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-white text-xs font-bold">
                            GC
                          </div>
                          <div className="truncate">
                            <p className="font-medium text-zinc-900 truncate">
                              {customer.name || customer.email || `GC #${(customer.id || '').slice(-6)}`}
                            </p>
                            <p className="text-xs text-zinc-500 truncate">
                              {customer.email && customer.email !== customer.name
                                ? customer.email
                                : (customer.address || customer.id)}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Arrow */}
                      <div className="text-zinc-400">
                        <ChevronRight size={20} />
                      </div>
                      
                      {/* CRM Match */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                            {match.name?.charAt(0) || '?'}
                          </div>
                          <div className="truncate">
                            <p className="font-medium text-zinc-900 truncate">{match.name}</p>
                            <p className="text-xs text-zinc-500 truncate">{match.email || 'CRM Customer'}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Match Score */}
                      <div className={`px-3 py-1 rounded-full text-sm font-medium flex-shrink-0 ${
                        match.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                        match.match_score >= 50 ? 'bg-teal-100 text-teal-700' :
                        'bg-zinc-100 text-zinc-600'
                      }`}>
                        {match.match_score}%
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-200 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-3 text-zinc-600 hover:text-zinc-800 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleLinkSelected}
            disabled={selectedMatches.size === 0}
            className="px-8 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-zinc-300 text-white font-semibold rounded-xl transition-colors flex items-center gap-2"
          >
            <Link2 size={18} />
            Link {selectedMatches.size} Selected
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Sync Page
const GoCardlessCustomerSync = ({ onBack }) => {
  const [gcCustomers, setGcCustomers] = useState([]);
  const [crmCustomers, setCrmCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [linkingCustomer, setLinkingCustomer] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [ignoredIds, setIgnoredIds] = useState(new Set());
  const [showMatchPreview, setShowMatchPreview] = useState(false);
  const [isLinkingAll, setIsLinkingAll] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    setLoadingProgress(0);
    
    try {
      const token = api.getToken();
      
      // Fetch ONLY first page (100 customers) — large GC accounts (10k+) hang the
      // browser if we paginate everything. Use "Load more" button for additional pages.
      const gcRes = await fetch(`${API_URL}/api/integrations/gocardless/customers?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!gcRes.ok) {
        const errorData = await gcRes.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load GoCardless customers. Is GoCardless connected?');
      }
      
      const gcData = await gcRes.json();
      const firstPage = gcData.customers || [];
      
      setGcCustomers(firstPage);
      setHasMore(Boolean(gcData.has_more));
      setNextCursor(gcData.after || null);
      setLoadingProgress(firstPage.length);
      
      // Load CRM customers
      const crmRes = await fetch(`${API_URL}/api/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (crmRes.ok) {
        const crmData = await crmRes.json();
        setCrmCustomers(crmData || []);
      }
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMoreCustomers = async () => {
    if (!hasMore || !nextCursor || isLoadingMore) return;
    
    setIsLoadingMore(true);
    setError(null);
    
    try {
      const token = api.getToken();
      const gcRes = await fetch(
        `${API_URL}/api/integrations/gocardless/customers?limit=100&after=${nextCursor}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (!gcRes.ok) {
        const errorData = await gcRes.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to load more customers');
      }
      
      const gcData = await gcRes.json();
      const nextPage = gcData.customers || [];
      
      setGcCustomers(prev => [...prev, ...nextPage]);
      setHasMore(Boolean(gcData.has_more));
      setNextCursor(gcData.after || null);
    } catch (err) {
      console.error('Error loading more customers:', err);
      setError(err.message);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleLink = async (gcCustomerId, crmCustomerId) => {
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/integrations/gocardless/customers/${gcCustomerId}/link`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ crm_customer_id: crmCustomerId })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to link customer');
      }
      
      // Update local state
      setGcCustomers(prev => 
        prev.map(c => c.id === gcCustomerId ? { ...c, is_linked: true } : c)
      );
      
      setSuccessMessage('Customer linked successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleIgnore = (gcCustomerId) => {
    setIgnoredIds(prev => new Set([...prev, gcCustomerId]));
  };

  const handleUndoIgnore = (gcCustomerId) => {
    setIgnoredIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(gcCustomerId);
      return newSet;
    });
  };

  // Reusable bulk-link helper. Accepts an array of {gcId, crmId, gcName?, crmName?}.
  // Used by both the Review All Matches modal and the Auto-link 80%+ button.
  const bulkLink = async (matchesToLink) => {
    if (!matchesToLink || matchesToLink.length === 0) return { success: 0, errors: 0 };
    setIsLinkingAll(true);
    try {
      const token = api.getToken();
      const links = matchesToLink.map(m => ({ gc_id: m.gcId, crm_id: m.crmId }));
      const res = await fetch(`${API_URL}/api/integrations/gocardless/customers/bulk-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ links })
      });
      const result = await res.json();
      if (res.ok) {
        const failedGcIds = new Set((result.errors || []).map(e => e.gc_id));
        const linkedGcIds = matchesToLink
          .map(m => m.gcId)
          .filter(id => !failedGcIds.has(id));
        setGcCustomers(prev => prev.map(c => linkedGcIds.includes(c.id) ? { ...c, is_linked: true } : c));
        const ok = result.success_count ?? result.linked_count ?? linkedGcIds.length;
        const fail = result.error_count ?? (result.errors?.length ?? 0);
        setSuccessMessage(`Linked ${ok} customer${ok === 1 ? '' : 's'}${fail > 0 ? `, ${fail} failed` : ''}`);
        return { success: ok, errors: fail };
      }
      setError(result.detail || 'Failed to link customers');
      return { success: 0, errors: matchesToLink.length };
    } catch (err) {
      console.error('Error bulk linking customers:', err);
      setError('Failed to link customers. Please try again.');
      return { success: 0, errors: matchesToLink.length };
    } finally {
      setIsLinkingAll(false);
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  // High-confidence auto-link: any pending GC customer whose top suggested match is >=80% and not already linked
  const autoLinkHighConfidence = async () => {
    const candidates = gcCustomers
      .filter(c => !c.is_linked && c.potential_match && !c.potential_match.already_linked && (c.potential_match.match_score ?? 0) >= 80)
      .map(c => ({
        gcId: c.id,
        gcName: c.name || c.email,
        crmId: c.potential_match.id,
        crmName: c.potential_match.name,
        score: c.potential_match.match_score
      }));
    if (candidates.length === 0) {
      setError('No 80%+ confidence matches found in the current page. Try "Load more" or "Review All Matches".');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (!window.confirm(`Auto-link ${candidates.length} customer${candidates.length === 1 ? '' : 's'} with 80%+ match confidence?\n\nYou can still skip or unlink individually after.`)) {
      return;
    }
    await bulkLink(candidates);
  };

  // Filter customers
  const filteredCustomers = gcCustomers.filter((customer) => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        (customer.name || '').toLowerCase().includes(search) ||
        (customer.email || '').toLowerCase().includes(search) ||
        (customer.id || '').toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }
    
    const isIgnored = ignoredIds.has(customer.id);
    
    switch (filter) {
      case 'pending':
        return !customer.is_linked && !isIgnored;
      case 'linked':
        return customer.is_linked;
      case 'ignored':
        return isIgnored;
      case 'all':
      default:
        return true;
    }
  });

  // Stats
  const stats = {
    total: gcCustomers.length,
    linked: gcCustomers.filter(c => c.is_linked).length,
    pending: gcCustomers.filter(c => !c.is_linked && !ignoredIds.has(c.id)).length,
    ignored: ignoredIds.size
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto mb-3" />
          <p className="text-zinc-600">Loading GoCardless customers...</p>
          <p className="text-zinc-500 text-xs mt-2">Fetching first 100 customers</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="gocardless-customer-sync-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 text-white" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="px-6 py-5">
          <div className="flex items-center gap-4">
            {onBack && (
              <button 
                onClick={onBack}
                className="p-2 hover:bg-white/10 rounded-xl transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center text-sm font-bold">GC</span>
                GoCardless Customer Sync
              </h1>
              <p className="text-zinc-400 text-sm">Link GoCardless customers to your CRM records</p>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 mt-6">
            <button
              onClick={() => setFilter('pending')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'pending' 
                  ? 'bg-amber-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.pending}</p>
              <p className={`text-sm ${filter === 'pending' ? 'text-amber-100' : 'text-zinc-400'}`}>Pending</p>
            </button>
            
            <button
              onClick={() => setFilter('linked')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'linked' 
                  ? 'bg-emerald-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.linked}</p>
              <p className={`text-sm ${filter === 'linked' ? 'text-emerald-100' : 'text-zinc-400'}`}>Linked</p>
            </button>
            
            <button
              onClick={() => setFilter('ignored')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'ignored' 
                  ? 'bg-zinc-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.ignored}</p>
              <p className={`text-sm ${filter === 'ignored' ? 'text-zinc-300' : 'text-zinc-400'}`}>Skipped</p>
            </button>
            
            <button
              onClick={() => setFilter('all')}
              className={`p-4 rounded-2xl transition-all ${
                filter === 'all' 
                  ? 'bg-teal-500 text-white' 
                  : 'bg-white/5 hover:bg-white/10'
              }`}
            >
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className={`text-sm ${filter === 'all' ? 'text-teal-100' : 'text-zinc-400'}`}>Total</p>
            </button>
          </div>
          
          {/* Action Buttons */}
          {stats.pending > 0 && (
            <div className="mt-4 space-y-2">
              {(() => {
                const highConfidenceCount = gcCustomers.filter(
                  c => !c.is_linked && c.potential_match && !c.potential_match.already_linked && (c.potential_match.match_score ?? 0) >= 80
                ).length;
                const previewCount = gcCustomers.filter(
                  c => c.potential_match && !c.potential_match.already_linked && !c.is_linked
                ).length;
                return (
                  <>
                    {highConfidenceCount > 0 && (
                      <button
                        onClick={autoLinkHighConfidence}
                        disabled={isLinkingAll}
                        data-testid="auto-link-high-confidence-btn"
                        className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 text-white font-semibold rounded-2xl transition-all shadow-lg"
                      >
                        <Zap size={22} />
                        Auto-link {highConfidenceCount} high-confidence match{highConfidenceCount === 1 ? '' : 'es'} (80%+)
                      </button>
                    )}
                    <button
                      onClick={() => setShowMatchPreview(true)}
                      data-testid="review-matches-btn"
                      className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold rounded-2xl transition-all shadow-lg"
                    >
                      <Eye size={22} />
                      Review All Matches ({previewCount} found)
                    </button>
                    <p className="text-center text-zinc-400 text-xs mt-2">
                      {highConfidenceCount > 0
                        ? 'Auto-link clears the obvious matches; review handles the rest'
                        : 'Preview all suggested matches before linking'}
                    </p>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6">
          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl text-red-700 flex items-center gap-3">
              <AlertCircle size={20} />
              <p>{error}</p>
              <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-red-100 rounded-lg">
                <X size={16} />
              </button>
            </div>
          )}
          
          {successMessage && (
            <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl text-emerald-700 flex items-center gap-3">
              <Check size={20} />
              <p>{successMessage}</p>
            </div>
          )}

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or GoCardless ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-zinc-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
              data-testid="search-input"
            />
          </div>

          {/* Customer List */}
          <div className="space-y-4">
            {filteredCustomers.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-zinc-200">
                <Users size={48} className="mx-auto mb-4 text-zinc-300" />
                <p className="text-zinc-500 text-lg">No customers to show</p>
                <p className="text-zinc-400 text-sm mt-1">
                  {filter === 'pending' && 'All customers have been processed!'}
                  {filter === 'linked' && 'No linked customers yet'}
                  {filter === 'ignored' && 'No skipped customers'}
                </p>
              </div>
            ) : (
              filteredCustomers.map((customer) => {
                const isIgnored = ignoredIds.has(customer.id);
                const suggestedMatches = customer.suggested_matches || [];
                const hasGoodMatch = customer.potential_match && !customer.potential_match.already_linked;
                
                return (
                  <div 
                    key={customer.id}
                    data-testid={`customer-card-${customer.id}`}
                    className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-all ${
                      customer.is_linked ? 'border-emerald-200 bg-emerald-50/30' :
                      isIgnored ? 'border-zinc-200 opacity-60' :
                      hasGoodMatch ? 'border-teal-200 bg-teal-50/30' :
                      'border-zinc-200 hover:border-zinc-300'
                    }`}
                  >
                    {/* Customer Info */}
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        {/* GoCardless Icon */}
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0">
                          GC
                        </div>
                        
                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <h3 className="font-semibold text-zinc-900 text-lg">
                                {customer.name || customer.email || `GC #${(customer.id || '').slice(-6)}`}
                              </h3>
                              <p className="text-zinc-500 text-sm">
                                {customer.email && customer.email !== customer.name
                                  ? customer.email
                                  : 'No email'}
                              </p>
                              {customer.address && (
                                <p className="text-zinc-400 text-sm mt-1 truncate">{customer.address}</p>
                              )}
                            </div>
                            
                            {/* Status Badge */}
                            <div className="flex-shrink-0">
                              {customer.is_linked ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-sm font-medium rounded-full">
                                  <Check size={14} />
                                  Linked
                                </span>
                              ) : isIgnored ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-400 text-white text-sm font-medium rounded-full">
                                  <EyeOff size={14} />
                                  Skipped
                                </span>
                              ) : hasGoodMatch ? (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-500 text-white text-sm font-medium rounded-full">
                                  <Sparkles size={14} />
                                  Match Found
                                </span>
                              ) : null}
                            </div>
                          </div>
                          
                          {/* Suggested Matches Section */}
                          {!customer.is_linked && !isIgnored && suggestedMatches.length > 0 && (
                            <div className="mt-4 p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                              <p className="text-sm text-teal-700 font-semibold flex items-center gap-2 mb-2">
                                <Sparkles size={16} />
                                Suggested Matches ({suggestedMatches.length})
                                <span className="text-xs font-normal text-teal-600">- Click to link</span>
                              </p>
                              {/* Show info if all matches are already linked */}
                              {suggestedMatches.every(m => m.already_linked) && (
                                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                                  <p className="text-xs text-amber-700 flex items-center gap-1">
                                    <AlertCircle size={12} />
                                    This may be a duplicate GoCardless customer. All matching CRM customers are already linked to other GoCardless IDs.
                                  </p>
                                </div>
                              )}
                              <div className="space-y-2">
                                {suggestedMatches.map((match, idx) => (
                                  <div 
                                    key={match.id} 
                                    className={`flex items-center justify-between p-2 rounded-lg transition-all ${
                                      match.already_linked 
                                        ? 'bg-amber-100/50 border border-amber-200 opacity-70' 
                                        : 'bg-white/80 border border-teal-200 hover:border-teal-400 hover:shadow-sm cursor-pointer'
                                    }`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!match.already_linked) {
                                        handleLink(customer.id, match.id);
                                      }
                                    }}
                                    data-testid={`suggested-match-${customer.id}-${match.id}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                        match.already_linked 
                                          ? 'bg-amber-200 text-amber-700' 
                                          : 'bg-teal-200 text-teal-700'
                                      }`}>
                                        {idx + 1}
                                      </span>
                                      <div>
                                        <p className="text-sm font-medium text-zinc-800">{match.name}</p>
                                        <p className="text-xs text-zinc-500">{match.email}</p>
                                        {match.already_linked && (
                                          <p className="text-xs text-amber-600 mt-0.5">Already linked to another GC customer</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                        match.match_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                        match.match_score >= 50 ? 'bg-teal-100 text-teal-700' :
                                        'bg-zinc-100 text-zinc-600'
                                      }`}>
                                        {match.match_score}%
                                      </span>
                                      {match.already_linked ? (
                                        <span className="text-xs px-2 py-1 rounded-lg bg-amber-200 text-amber-700 font-medium flex items-center gap-1">
                                          <AlertCircle size={12} />
                                          Unavailable
                                        </span>
                                      ) : (
                                        <span className="text-xs px-3 py-1 rounded-lg bg-teal-500 text-white font-medium flex items-center gap-1 hover:bg-teal-600">
                                          <Link2 size={12} />
                                          Link
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    {!customer.is_linked && (
                      <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-end gap-3">
                        {isIgnored ? (
                          <button
                            onClick={() => handleUndoIgnore(customer.id)}
                            className="px-4 py-2 text-zinc-600 hover:text-zinc-800 text-sm font-medium flex items-center gap-2"
                          >
                            <RefreshCw size={16} />
                            Restore
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() => handleIgnore(customer.id)}
                              className="px-4 py-2 text-zinc-500 hover:text-zinc-700 text-sm font-medium flex items-center gap-2"
                              data-testid={`skip-btn-${customer.id}`}
                            >
                              <EyeOff size={16} />
                              Skip
                            </button>
                            <button
                              onClick={() => setLinkingCustomer(customer)}
                              className="px-5 py-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl flex items-center gap-2"
                              data-testid={`link-btn-${customer.id}`}
                            >
                              <Link2 size={16} />
                              Find Match
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
          
          {/* Load more button (only shown when GoCardless has more pages) */}
          {hasMore && filteredCustomers.length > 0 && !searchTerm && (
            <div className="mt-6 text-center">
              <button
                onClick={loadMoreCustomers}
                disabled={isLoadingMore}
                data-testid="load-more-gc-btn"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-zinc-300 hover:border-teal-400 hover:bg-teal-50 disabled:opacity-50 text-zinc-700 font-medium rounded-2xl transition-all"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Loading more...
                  </>
                ) : (
                  <>
                    <ChevronDown size={18} />
                    Load next 100 GoCardless customers
                  </>
                )}
              </button>
              <p className="text-xs text-zinc-500 mt-2">
                Showing {gcCustomers.length} loaded · more available on GoCardless
              </p>
            </div>
          )}
          
          {/* Hint: when searching but more pages exist on GoCardless side */}
          {hasMore && searchTerm && (
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-amber-800 text-sm">
              <strong>Not finding the customer?</strong> Only {gcCustomers.length} customers loaded so far.
              Clear the search and click "Load next 100" to fetch more from GoCardless.
            </div>
          )}
        </div>
      </div>

      {/* Match Preview Modal */}
      {showMatchPreview && (
        <MatchPreviewModal
          gcCustomers={gcCustomers}
          onClose={() => setShowMatchPreview(false)}
          onLinkAll={async (matchesToLink) => {
            setShowMatchPreview(false);
            await bulkLink(matchesToLink);
          }}
        />
      )}

      {/* Linking All Progress Overlay */}
      {isLinkingAll && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-500 mx-auto mb-4" />
            <p className="text-lg font-semibold text-zinc-900">Linking customers...</p>
            <p className="text-zinc-500 text-sm mt-1">Please wait while we link all matches</p>
          </div>
        </div>
      )}

      {/* Find Match Modal */}
      {linkingCustomer && (
        <FindMatchModal
          gcCustomer={linkingCustomer}
          onClose={() => setLinkingCustomer(null)}
          onLink={handleLink}
        />
      )}
    </div>
  );
};

export default GoCardlessCustomerSync;
